import React, { useRef } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart as RechartsLineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  CHART_COLOR,
  CHART_SERIES_COLORS,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/constants';
import { TruncatedXAxisTick } from '@/features/ai-agents/chat/MarkdownTableVisualization/helpers';
import {
  ChartRow,
  ChartSeries,
  ChartValueFormatter,
  CompareLineChartData,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/types';
import { useChartContainerWidth } from '@/features/ai-agents/chat/MarkdownTableVisualization/use-chart-container-width';
import {
  createVariantLineChartData,
  excludeTotalRows,
  excludeTotalRowsFromSeries,
  getChartXAxisInterval,
  sortChartRowsByDate,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/utils';

interface LineChartProps {
  compareData?: CompareLineChartData | null;
  data: ChartRow[];
  formatValue: ChartValueFormatter;
  series: ChartSeries[];
  valueLabel: string;
}

export function LineChart({
  compareData,
  data,
  formatValue,
  series,
  valueLabel,
}: LineChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartWidth = useChartContainerWidth(containerRef);

  if (compareData) {
    return (
      <div ref={containerRef}>
        <ResponsiveContainer width="100%" height={300}>
          <RechartsLineChart
            data={compareData.rows}
            margin={{ top: 12, right: 20, bottom: 8, left: 0 }}
          >
            <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
            <XAxis
              dataKey="x"
              tick={<TruncatedXAxisTick />}
              interval={getChartXAxisInterval(
                compareData.rows.length,
                chartWidth,
              )}
              height={72}
              allowDuplicatedCategory={false}
            />
            <YAxis tickFormatter={formatValue} />
            <Tooltip formatter={formatValue} />
            <Legend />
            {compareData.series.map((chartSeries, index) => (
              <Line
                key={chartSeries.dataKey}
                type="monotone"
                dataKey={chartSeries.dataKey}
                name={chartSeries.name}
                stroke={CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length]}
                strokeWidth={2}
                strokeDasharray={chartSeries.dashed ? `6 4` : undefined}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                connectNulls
                isAnimationActive={false}
              />
            ))}
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  const variantLineChartData = createVariantLineChartData(
    excludeTotalRowsFromSeries(series),
  );
  const hasMultipleSeries = variantLineChartData.series.length > 0;
  const chartData = hasMultipleSeries
    ? sortChartRowsByDate(variantLineChartData.rows)
    : sortChartRowsByDate(excludeTotalRows(data));

  return (
    <div ref={containerRef}>
      <ResponsiveContainer width="100%" height={300}>
        <RechartsLineChart
          data={chartData}
          margin={{ top: 12, right: 20, bottom: 8, left: 0 }}
        >
          <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
          <XAxis
            dataKey="x"
            tick={<TruncatedXAxisTick />}
            interval={getChartXAxisInterval(chartData.length, chartWidth)}
            height={72}
            allowDuplicatedCategory={false}
          />
          <YAxis tickFormatter={formatValue} />
          <Tooltip formatter={formatValue} />
          {hasMultipleSeries && <Legend />}
          {hasMultipleSeries ? (
            variantLineChartData.series.map((chartSeries, index) => (
              <Line
                key={chartSeries.name}
                type="monotone"
                dataKey={chartSeries.dataKey}
                name={chartSeries.name}
                stroke={CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                connectNulls
                isAnimationActive={false}
              />
            ))
          ) : (
            <Line
              type="monotone"
              dataKey="y"
              name={valueLabel}
              stroke={CHART_COLOR}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              connectNulls
              isAnimationActive={false}
            />
          )}
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  );
}
